import api from '../api';
import type { IBatch } from '../../types/batch';
// ✅ UPDATED: Import the specific populated enrollment type
import type { IPopulatedEnrollment } from '../../types/enrollment';

// --- Interfaces for function payloads ---
interface CreateBatchPayload {
  name: string;
  courseId: string;
  month: string;
  year: number;
  startDate: string;
  endDate: string;
  capacity?: number;
}

interface UpdateBatchPayload {
    name?: string;
    startDate?: string;
    endDate?: string;
    capacity?: number;
    isActive?: boolean;
}

// --- Service Functions ---

export const createBatch = async (payload: CreateBatchPayload): Promise<IBatch> => {
  try {
    const response = await api.post<{ batch: IBatch }>('/batches', payload);
    return response.data.batch;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create batch.');
  }
};

export const getAllBatches = async (): Promise<IBatch[]> => {
  try {
    const response = await api.get<IBatch[]>('/batches');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch batches.');
  }
};

export const getBatchStudents = async (batchId: string): Promise<IPopulatedEnrollment[]> => {
  try {
    // ✅ UPDATED: The return type is now more specific and accurate
    const response = await api.get<IPopulatedEnrollment[]>(`/batches/${batchId}/students`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch batch students.');
  }
};

export const updateBatch = async (id: string, payload: UpdateBatchPayload): Promise<IBatch> => {
    try {
        const response = await api.put<{ batch: IBatch }>(`/batches/${id}`, payload);
        return response.data.batch;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || 'Failed to update batch.');
    }
};

export const deleteBatch = async (id: string): Promise<string> => {
  try {
    const response = await api.delete<{ message: string }>(`/batches/${id}`);
    return response.data.message;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete batch.');
  }
};

export const addEnrollmentToBatch = async (batchId: string, enrollmentId: string): Promise<string> => {
  try {
    const response = await api.patch<{ message: string }>(`/batches/${batchId}/add-enrollment`, { enrollmentId });
    return response.data.message;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to add enrollment to batch.');
  }
};

export const exportBatch = async (batchId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<void> => {
    try {
        const response = await api.get(`/batches/${batchId}/export`, {
            params: { format },
            responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        let filename = `batch-roster.${format}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch?.[1]) {
                filename = filenameMatch[1];
            }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error: any) {
        throw new Error(error.response?.data?.message || `Failed to export batch as ${format}.`);
    }
};

// ✨ NEW: Service function to get all batches for a specific course





export const getBatchesByCourse = async (courseId: string): Promise<IBatch[]> => {
  try {
    // Note: You need a corresponding backend route for this to work
    const response = await api.get<IBatch[]>(`/courses/${courseId}/batches`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch batches for course.');
  }
};

export const getBatchById = async (batchId: string): Promise<IBatch> => {
  try {
    // We expect the API to return the IBatch object directly
    const response = await api.get<IBatch>(`/batches/${batchId}`);
    return response.data; // ✅ Corrected: Use the data directly
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch batch details.');
  }
};


const batchService = {
    createBatch,
    getAllBatches,
    getBatchStudents,
    updateBatch,
    deleteBatch,
    addEnrollmentToBatch,
    exportBatch,
    getBatchesByCourse,
    getBatchById,
};

export default batchService;